package com.crowpay.views.dialogs

import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.SignalingList
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.views.components.field2
import com.crowpay.views.components.files.FileData
import com.crowpay.views.components.files.FilePreview
import com.crowpay.views.components.files.fileCarousel
import com.crowpay.views.components.label2
import com.crowpay.views.screens.common.secondDialog
import com.crowpay.views.theming.PrimaryButtonSemantic
import com.crowpay.views.theming.body
import com.crowpay.views.theming.secondaryButton
import com.crowpay.views.theming.smallBody
import com.crowpay.views.theming.specCenteredText
import com.crowpay.views.theming.tertiaryButton
import com.crowpay.views.theming.xlTitle
import com.lightningkite.kiteui.ExternalServices
import com.lightningkite.kiteui.models.ThemeDerivation
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.bind
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.atEnd
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.dialog
import com.lightningkite.kiteui.views.direct.RowOrCol
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.dismissBackground
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.rowCollapsingToColumn
import com.lightningkite.kiteui.views.direct.scrolls
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.direct.swapView
import com.lightningkite.kiteui.views.direct.swapping
import com.lightningkite.kiteui.views.direct.textArea
import com.lightningkite.kiteui.views.expanding
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll

abstract class BaseTextInputDialog(
    val header: String,
    val message: String,
    val confirmationText: String,
    val confirmationTheme: ThemeDerivation = PrimaryButtonSemantic,
    val minChars: Int = 0,
    val hint: String = if (minChars > 0) "Min $minChars characters" else "",
) : Page {
    val userMessage = Property("")

    protected abstract suspend fun ViewWriter.submit(confirmed: Boolean)

    open fun ViewWriter.dialogCol(contents: RowOrCol.() -> Unit): ViewModifiable = scrolls - dismissBackground {
        spacing = AppDimensions.fullIndent
        onClick { submit(false) }
        secondDialog.value = null
        centered - col {
            space()
            dialog - sizeConstraints(width = 45.rem) - col {
                spacing = AppDimensions.majorColumnSpacing
                contents()
            }
            space()
        }
        swapView {
            swapping(
                current = { secondDialog() },
                views = { it?.run { render2() } }
            )
        }
    }

    open fun ViewWriter.header(): ViewModifiable = xlTitle(header)

    open fun ViewWriter.message(): ViewModifiable = body(message)

    open fun ViewWriter.textInput(): ViewModifiable {
        fun input() = field2("Message") {
            sizeConstraints(minHeight = 8.rem) - textArea {
                this@textArea.hint = this@BaseTextInputDialog.hint
                content bind userMessage
            }
        }

        return if (minChars > 0) col {
            spacing = 0.2.rem
            input()
            atEnd - smallBody {
                ::content { (minChars - userMessage().length).takeIf { it > 0 }?.let { "($it) more characters required" } ?: "" }
            }
        }
        else input()
    }

    open fun ViewWriter.dismissButton(): ViewModifiable =
        secondaryButton - button {
            specCenteredText("Go Back")
            onClick("Go Back") {
                submit(false)
            }
        }

    open fun ViewWriter.submitButton(): ViewModifiable =
        confirmationTheme.onNext - button {
            if (minChars > 0) ::enabled { userMessage().length >= minChars }
            specCenteredText(confirmationText)
            onClick(confirmationText) {
                submit(true)
            }
        }

    open fun ViewWriter.actionBar(): ViewModifiable = row {
        submitButton()
        dismissButton()
    }

    override fun ViewWriter.render2(): ViewModifiable = dialogCol {
        header()
        message()
        textInput()
        actionBar()
    }
}

class TextInputDialog(
    header: String,
    message: String,
    confirmationText: String,
    confirmationTheme: ThemeDerivation = PrimaryButtonSemantic,
    minChars: Int = 0,
    hint: String = if (minChars > 0) "Min $minChars characters" else "",
    val onSubmit: suspend (String?) -> Unit
) : BaseTextInputDialog(header, message, confirmationText, confirmationTheme, minChars, hint) {

    override suspend fun ViewWriter.submit(confirmed: Boolean) =
        if (confirmed) onSubmit(userMessage.value) else onSubmit(null)
}

class TextAndFileInputDialog(
    header: String,
    message: String,
    confirmationText: String,
    confirmationTheme: ThemeDerivation = PrimaryButtonSemantic,
    minChars: Int = 0,
    hint: String = if (minChars > 0) "Min $minChars characters" else "",
    val onSubmit: suspend ViewWriter.(Submission?) -> Unit
) : BaseTextInputDialog(header, message, confirmationText, confirmationTheme, minChars, hint) {
    data class Submission(
        val message: String,
        val files: List<FileData>
    )

    val files = SignalingList<FileData>()

    override suspend fun ViewWriter.submit(confirmed: Boolean): Unit =
        if (confirmed) onSubmit(Submission(userMessage.value, files))
        else onSubmit(null)

    override fun ViewWriter.actionBar(): ViewModifiable = rowCollapsingToColumn(AppDimensions.normalCollapseWidth) {
        tertiaryButton - button {
            specCenteredText("+ Attachment")
            onClick("Upload Attachments") {
                ExternalServices.requestFiles()
                    .map { file ->
                        val preview = FilePreview.LocalFile(file)
                        files.add(
                            FileData(
                                name = Property(""),
                                preview = preview,
                                original = preview
                            )
                        )
                        async {
                            preview.uploadAttachment(notNullSession().nonCached.incident::uploadFileForRequest)
                        }
                    }
                    .awaitAll()
            }
        }
        expanding - space()
        dismissButton()
        submitButton()
    }

    override fun ViewWriter.render2(): ViewModifiable = dialogCol {
        header()
        message()

        textInput()

        label2("Attachments") {
            existsDefaultFalse { files().isNotEmpty() }
            fileCarousel(files)
        }

        actionBar()
    }
}